/* eslint-disable no-console */
import store from './redux/store';
import { split, ApolloLink, HttpLink, fromPromise } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { ApolloClient } from '@apollo/client/core';
import { InMemoryCache } from '@apollo/client/cache';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import api from './gql/api';
import errorHandler from './gql/errorHandler';
import history from './routes/history';
import { PATH_PAGE } from './routes/paths';
import SnackbarUtils from './utilities/SnackbarUtilsConfigurator';
import I18n from './i18n';
import { tourPositionsPaginationConfig } from './utilities/apolloClientConfig';

const defaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
  },
  query: {
    fetchPolicy: 'no-cache',
  },
  mutate: {
    fetchPolicy: 'no-cache',
  },
  subscription: {
    fetchPolicy: 'no-cache',
  },
};

// graphql-ws re-arms its keepAlive ping only when a pong comes back, and has no
// pong timeout of its own. So if the server stops answering, the client sends a
// single ping into the void and then goes quiet forever without ever concluding
// the connection is dead -- it relies entirely on the browser firing onclose.
// That is fine for a hard disconnect, but on a silent partition (captive
// portal, black-holing router, half-dead VPN) the browser can take minutes or
// never fire it, while the server detected the dead socket in 12-24s and
// already released the operator's situations. The operator is left staring at a
// live-looking screen listing situations that now belong to someone else.
//
// This is the pong timeout from the graphql-ws docs: terminate() emits a
// retryable close (4499) so the normal reconnect loop takes over.
//
// Kept above the server's own 12s keepAlive so a single slow beat does not trip
// it. The Pong is answered by the subscriptions event loop, so a pod stalling
// under CPU pressure would otherwise have every operator's tab terminate at
// once and reconnect together, feeding the stall.
const PONG_TIMEOUT_MS = 20000;

const createAutorestartWSClient = () => {
  let pongTimeout;
  const clearPongTimeout = () => {
    clearTimeout(pongTimeout);
    pongTimeout = undefined;
  };

  const client = createClient({
    url: import.meta.env.VITE_APP_GRAPHQL_SUBSCRIPTION_SERVER || '',
    connectionParams: () => ({
      authToken: localStorage.getItem('token'),
      monitoringToken: sessionStorage.getItem('monitoring_session') || null,
      mapsToken: sessionStorage.getItem('maps_session') || null,
      workspaceId: store.getState().currentWorkspace.id || null,
    }),
    keepAlive: 5000,
    // Without this the client waits forever when the server accepts the socket
    // but never acknowledges the connection.
    connectionAckWaitTimeout: 15000,
    // Default is 5 attempts over ~40s, after which the client gives up FOREVER
    // and nothing recreates it -- the tab goes silent (monitoring, maps,
    // connector status) until a reload, leaving no trace in server logs. Retry
    // indefinitely instead. Fatal close codes still throw before any of this is
    // consulted, so genuine rejections keep surfacing rather than hiding in a
    // silent loop.
    retryAttempts: Infinity,
    // retryAttempts alone is not enough. The default `shouldRetry` is
    // `isLikeCloseEvent`, which requires both `code` and `reason` on the value:
    // true for a close event, false for the plain Event a browser raises when a
    // connection attempt FAILS to establish -- exactly what every retry during
    // an outage hits. With the default, such an attempt is refused as
    // non-retryable no matter how high retryAttempts is.
    //
    // Retry everything that gets this far. Genuinely terminal cases never do:
    // fatal close codes (4500, 4401, 4400, 4409, 4429, every 1000-1999 internal
    // code) are thrown earlier, before shouldRetry is consulted, so a revoked
    // token or a rejected operator still surfaces instead of looping.
    //
    // Do NOT narrow this to `!(e instanceof Error)`. The frontend pins
    // graphql-ws 5.14.3, where terminate() emits a plain {code, reason} object
    // and that predicate happens to work -- but from 5.15 it is a
    // TerminatedCloseEvent that extends Error, so the same predicate silently
    // inverts and refuses to reconnect after the very detection this client
    // adds, and breaks the terminate() call in pages/Monitoring/index.jsx too.
    // A dependency bump would turn that on with nothing to catch it.
    shouldRetry: () => true,
    // The default backoff is exponential with NO ceiling: attempt 10 waits 17
    // minutes and attempt 20 waits 12 days, which makes "infinite" useless in
    // practice. Cap the base at 30s so a stuck client settles at about one
    // attempt per minute instead of drifting away.
    retryWait: async (retries) => {
      // Floored as well as capped. graphql-ws resets `retries` on every
      // ConnectionAck, so a server that accepts and then drops the socket never
      // escalates its backoff and would loop at ~1.5s per tab indefinitely.
      const delay = Math.min(30000, Math.max(3000, 1000 * 2 ** retries));
      // Jitter proportional to the delay rather than the library default's flat
      // 300ms-3s window, which at a 30s base spreads reconnects across only
      // ~9% and would land every operator's tab in the same couple of seconds
      // after a subscriptions restart. Effective wait at the cap: 30-60s.
      await new Promise((resolve) => {
        setTimeout(resolve, delay + Math.random() * delay);
      });
    },
    on: {
      connected: clearPongTimeout,
      closed: clearPongTimeout,
      // `received` is false for the ping we just sent and true for one coming
      // from the server, which graphql-ws answers on its own and must not start
      // a timer.
      ping: (received) => {
        if (received) return;
        clearPongTimeout();
        pongTimeout = setTimeout(() => client.terminate(), PONG_TIMEOUT_MS);
      },
      pong: (received) => {
        if (received) clearPongTimeout();
      },
    },
  });

  return client;
};

export const websocketClient = createAutorestartWSClient();

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('token');
  if (token) {
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  }

  return null;
});

const monitoringSessionLink = new ApolloLink((operation, forward) => {
  const { current } = operation.getContext();
  if (current === 'monitoring') {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        monitoringToken: sessionStorage.getItem('monitoring_session') || null,
      },
    }));
  }
  return forward(operation);
});

const httpLink = ApolloLink.from([
  onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      // eslint-disable-next-line no-restricted-syntax
      for (const error of graphQLErrors) {
        switch (error.extensions.code) {
          case 'UNAUTHENTICATED':
            return fromPromise(
              api.auth.refreshTokens().then(({ data }) => {
                localStorage.setItem('token', data.refreshTokens.token);
                return data.refreshTokens.token;
              })
            )
              .filter((value) => Boolean(value))
              .flatMap((accessToken) => {
                const oldHeaders = operation.getContext().headers;

                operation.setContext({
                  headers: {
                    ...oldHeaders,
                    authorization: `Bearer ${accessToken}`,
                  },
                });

                return forward(operation);
              });
          case 'FORBIDDEN': {
            console.error(error);
            // Solo mostramos la barra cuando el usuario ejecuta activamente
            // una accion (mutation). Un FORBIDDEN en una query es una lectura
            // de fondo/enrichment (p.ej. situations privadas para un member) y
            // no debe alarmar: queda solo en consola. Override por operacion:
            // context: { forbiddenSnackbar: true|false }.
            const isMutation =
              getMainDefinition(operation.query)?.operation === 'mutation';
            const { forbiddenSnackbar } = operation.getContext();
            if (forbiddenSnackbar ?? isMutation) {
              SnackbarUtils.error(I18n.t('error.forbidden_permissions'));
            }
            break;
          }
          case 'REFRESH_TOKEN_EXPIRED':
            history.push(PATH_PAGE.logout);
            setTimeout(() => {
              SnackbarUtils.error(I18n.t('error.refreshTokenExpired'));
            }, 1000);
            break;
          case 'ONDELETE_RESTRICT':
            SnackbarUtils.error(I18n.t('error.restrict'));
            break;
          default:
            if (import.meta.env.VITE_APP_DEBUG === 'true') {
              errorHandler(error);
            }
        }
      }
    }

    if (networkError) {
      // toast({ text: errorHandler(networkError) });
    }

    return null;
  }),
  monitoringSessionLink,
  new HttpLink({
    uri: import.meta.env.VITE_APP_GRAPHQL_SERVER || '',
    credentials: 'include', // headers
  }),
]);

const wsLink = new GraphQLWsLink(websocketClient);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  authLink.concat(httpLink)
);

const apolloClient = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          getTourPositionsByDeviceId: tourPositionsPaginationConfig(),
        },
      },
    },
  }),
  defaultOptions,
});

export default apolloClient;
